import React, { createContext, useContext, useEffect, useState, useRef } from 'react';

interface GoogleMapsContextValue {
  isLoaded: boolean;
}

const GoogleMapsContext = createContext<GoogleMapsContextValue>({ isLoaded: false });

export function useGoogleMaps() {
  return useContext(GoogleMapsContext);
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

export function GoogleMapsProvider({ children }: { children: React.ReactNode }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const loadingRef = useRef(false);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      console.warn('[GoogleMaps] No API key found (VITE_GOOGLE_MAPS_API_KEY). Address autocomplete will fall back to manual entry.');
      return;
    }

    if (window.google?.maps?.places) {
      setIsLoaded(true);
      return;
    }

    if (loadingRef.current) return;
    loadingRef.current = true;

    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setIsLoaded(true));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);
    script.onerror = () => {
      console.error('[GoogleMaps] Failed to load Google Maps script');
      loadingRef.current = false;
    };
    document.head.appendChild(script);
  }, []);

  return (
    <GoogleMapsContext.Provider value={{ isLoaded }}>
      {children}
    </GoogleMapsContext.Provider>
  );
}
